import React, { useCallback, useEffect, useRef } from 'react';
import { NodeType } from '../../types';
import { formatDuration } from '../../utils/audioReference';
import { TONE_COLORS, nodeTone } from './nodeTheme';

interface AudioWaveformProps {
  /** Normalised peaks (0..1); null while the clip is still being decoded. */
  peaks: Float32Array | null;
  /** Decoding failed: a flat line is drawn and seeking still works. */
  failed?: boolean;
  /** Playback position as a fraction of the duration. */
  progress: number;
  durationSec?: number;
  currentSec: number;
  disabled?: boolean;
  onSeek: (fraction: number) => void;
  onTogglePlay?: () => void;
}

const BAR_WIDTH = 2;
const BAR_GAP = 1;
// Drawn on the ink panel of the Reference node. What has played takes that node's colour
// (nodeTheme holds the tones for what is drawn); everything else is cream.
const COLORS = {
  played: TONE_COLORS[nodeTone(NodeType.MEDIA_REFERENCE)].solid,
  unplayed: 'rgba(249, 246, 241, 0.34)', // cream at low alpha on ink
  pending: 'rgba(249, 246, 241, 0.14)',
  playhead: '#F9F6F1',                   // cream
};

/**
 * The clip drawn as mirrored bars on a canvas. Played bars turn the Reference node's
 * colour and a cream playhead marks the position. Click or drag to seek; with the
 * keyboard the bar is a slider (arrows seek, Space toggles playback).
 */
export const AudioWaveform: React.FC<AudioWaveformProps> = ({
  peaks, failed = false, progress, durationSec, currentSec, disabled = false, onSeek, onTogglePlay,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draggingRef = useRef(false);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const bars = Math.max(1, Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP)));
    const mid = height / 2;
    const usable = height - 6;
    const playedBars = progress * bars;
    const ready = !!peaks && peaks.length > 0;

    for (let i = 0; i < bars; i++) {
      let amplitude: number;
      if (ready) {
        const index = Math.min(peaks!.length - 1, Math.floor((i / bars) * peaks!.length));
        amplitude = peaks![index];
      } else if (failed) {
        amplitude = 0.05;
      } else {
        amplitude = 0.1 + 0.08 * Math.abs(Math.sin(i * 0.6)); // quiet placeholder while decoding
      }
      const barHeight = Math.max(2, amplitude * usable);
      const x = i * (BAR_WIDTH + BAR_GAP);
      const y = mid - barHeight / 2;
      ctx.fillStyle = !ready && !failed ? COLORS.pending : i < playedBars ? COLORS.played : COLORS.unplayed;
      if (typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(x, y, BAR_WIDTH, barHeight, 1);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, BAR_WIDTH, barHeight);
      }
    }

    if ((ready || failed) && progress > 0) {
      ctx.fillStyle = COLORS.playhead;
      ctx.fillRect(Math.min(width - 2, progress * width) - 0.5, 1, 2, height - 2);
    }
  }, [peaks, failed, progress]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => draw());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [draw]);

  const seekFromPointer = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    onSeek(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const step = durationSec && durationSec > 0 ? Math.min(0.5, 5 / durationSec) : 0.05;
    switch (e.key) {
      case 'ArrowLeft': e.preventDefault(); onSeek(Math.max(0, progress - step)); break;
      case 'ArrowRight': e.preventDefault(); onSeek(Math.min(1, progress + step)); break;
      case 'Home': e.preventDefault(); onSeek(0); break;
      case 'End': e.preventDefault(); onSeek(1); break;
      case ' ':
      case 'Enter': e.preventDefault(); onTogglePlay?.(); break;
      default: break;
    }
  };

  return (
    <canvas
      ref={canvasRef}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Playback position"
      aria-valuemin={0}
      aria-valuemax={Math.round(durationSec || 0)}
      aria-valuenow={Math.round(currentSec)}
      aria-valuetext={durationSec ? `${formatDuration(currentSec)} of ${formatDuration(durationSec)}` : formatDuration(currentSec)}
      aria-disabled={disabled}
      // On the ink panel the app's focus ring is drawn in the light lavender, as on every dark surface
      className="no-drag block w-full h-full touch-none rounded-lg cursor-pointer focus-visible:outline-lavender"
      onPointerDown={(e) => {
        if (disabled) return;
        draggingRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        seekFromPointer(e);
      }}
      onPointerMove={(e) => { if (draggingRef.current) seekFromPointer(e); }}
      onPointerUp={(e) => {
        draggingRef.current = false;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onPointerCancel={() => { draggingRef.current = false; }}
      onKeyDown={onKeyDown}
    />
  );
};

export default AudioWaveform;
